/**
 * Files → RFQs when the "RFQ requests" pilot is on.
 *
 *   1. Request rounds — newest first; each a grid of lanes × suppliers with Sent /
 *      Quoted $ / Not bidding / Approved chips, plus "Re-request" per list (and per
 *      round) that reopens the wizard on those lanes — flagged when the list has a
 *      newer version than the one sent. Approvals stay on the Suppliers tab this phase
 *      ("Approve in Suppliers").
 *   2. Earlier activity — pre-request sends, rendered by the old RFQ files view so no
 *      history disappears.
 */
import React, { useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { AlertTriangle, CalendarClock, CheckCircle2, ChevronRight, ExternalLink, Plus, RefreshCw, Send } from 'lucide-react';
import { storage } from '../../utils/firebase';
import type {
  ProjectFile,
  ProjectFileStream,
  RfqHubFileRelease,
  RfqPackage,
  RfqRequest,
  RfqSubmission,
  VendorFileRelease,
} from '../../types';
import { rfqRequestStageLabel } from '../../utils/rfqRequestStage';
import { RFQ_SUPPLIER_STATUS_LABELS, type RfqProjectApprovalFields, type RfqSupplierStatus } from '../../utils/projectRfqFiles';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import { formatQuoteTotal, submissionQuoteTotal } from '../../utils/rfqQuoteTotal';
import { buildRfqCoverage, isRequestOpen, packageSupplierStatus } from '../../utils/rfqRequestCoverage';
import { groupTimberReleases, laneReleaseTitle, laneReleaseVersionLabel } from '../../utils/rfqReleaseGroups';
import { supersededStructureArchiveRows } from '../../utils/rfqPackageArchivedStructures';
import { archivePackageStructureIds, restorePackageStructureIds } from '../../utils/rfqPackageArchiveStructures';
import ProjectRfqFilesView from './ProjectRfqFilesView';
import './ProjectFilesViews.css';
import './ProjectRfqRequestsView.css';

export interface ProjectRfqRequestsViewProps {
  requests: RfqRequest[];
  hubReleases: RfqHubFileRelease[];
  vendorReleases: VendorFileRelease[];
  packages: RfqPackage[];
  streams: ProjectFileStream[];
  files: ProjectFile[];
  structures: StructureRef[];
  supplierNames: Map<string, string>;
  submissions: RfqSubmission[];
  projectApprovals: Map<string, RfqProjectApprovalFields>;
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  onOpenSource: (file: ProjectFile) => void;
  onOpenStream: (streamId: string) => void;
  onOpenSuppliers?: () => void;
  /** Staff may send requests (gate + not read-only). */
  canRequest: boolean;
  /** Open the request wizard, optionally preselecting lanes by track or a set of staged releases. */
  onNewRequest: (opts?: { streamId?: string; streamIds?: string[]; releaseIds?: string[] }) => void;
}

const fmtDate = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const ProjectRfqRequestsView: React.FC<ProjectRfqRequestsViewProps> = ({
  requests,
  hubReleases,
  vendorReleases,
  packages,
  streams,
  files,
  structures,
  supplierNames,
  submissions,
  projectApprovals,
  loading,
  error,
  onRetry,
  onOpenSource,
  onOpenStream,
  onOpenSuppliers,
  canRequest,
  onNewRequest,
}) => {
  const lanes = useMemo(
    () => buildRfqCoverage({ structures, streams, files, requests, packages, hubReleases, submissions, projectById: projectApprovals }),
    [structures, streams, files, requests, packages, hubReleases, submissions, projectApprovals],
  );
  const laneByStreamId = useMemo(() => new Map(lanes.filter(l => l.stream?.id).map(l => [l.stream!.id!, l])), [lanes]);
  const packageById = useMemo(() => new Map(packages.filter(p => p.id).map(p => [p.id!, p])), [packages]);
  const fileById = useMemo(() => new Map(files.filter(f => f.id).map(f => [f.id!, f])), [files]);
  const releaseById = useMemo(() => new Map(hubReleases.filter(r => r.id).map(r => [r.id!, r])), [hubReleases]);
  const timberReleases = useMemo(() => hubReleases.filter(r => r.workType === 'timber_rfq'), [hubReleases]);
  // Staged by hand (Suppliers tab) but never sent — grouped per structure so three
  // independently staged barns read as three lists, not v1/v2/v3 of one.
  const stagedLanes = useMemo(
    () => groupTimberReleases({ releases: timberReleases, packages, requests }).staged,
    [timberReleases, packages, requests],
  );
  const stagedReleaseIds = useMemo(
    () => stagedLanes.flatMap(l => l.releases.map(r => r.id).filter((id): id is string => !!id)),
    [stagedLanes],
  );
  const openCount = useMemo(
    () => requests.filter(r => isRequestOpen(r, packages, submissions, projectApprovals)).length,
    [requests, packages, submissions, projectApprovals],
  );

  // Pre-request history: anything not carried by a request round.
  const requestPackageIds = useMemo(() => {
    const ids = new Set<string>();
    for (const r of requests) for (const l of r.lines) for (const id of Object.values(l.packageIdByOrg ?? {})) ids.add(id);
    for (const p of packages) if (p.rfqRequestId && p.id) ids.add(p.id);
    return ids;
  }, [requests, packages]);
  const legacyPackages = useMemo(() => packages.filter(p => !p.id || !requestPackageIds.has(p.id)), [packages, requestPackageIds]);
  const requestReleaseIds = useMemo(() => new Set(requests.flatMap(r => r.lines.map(l => l.hubFileReleaseId))), [requests]);
  const legacyReleases = useMemo(
    () => hubReleases.filter(r => !r.id || !requestReleaseIds.has(r.id) || legacyPackages.some(p => p.hubFileReleaseId === r.id)),
    [hubReleases, requestReleaseIds, legacyPackages],
  );
  const hasLegacy = legacyReleases.length + vendorReleases.length + legacyPackages.length > 0;
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [collapsedRounds, setCollapsedRounds] = useState<Set<string>>(() => new Set());
  const [archiveBusyId, setArchiveBusyId] = useState<string | null>(null);
  const toggleRound = (id: string) =>
    setCollapsedRounds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openQuote = async (sub: RfqSubmission) => {
    try {
      const url = await getDownloadURL(storageRef(storage, sub.storagePath));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('[ProjectRfqRequestsView] open quote failed', e);
      window.alert('Could not open the quote file.');
    }
  };

  const statusChip = (status: RfqSupplierStatus, total: number | null, quote?: RfqSubmission) => {
    const label = RFQ_SUPPLIER_STATUS_LABELS[status];
    return (
      <span className="pfrq-cell-status">
        <span className={`pfv-chip pfv-chip--rfq-${status}`}>
          {status === 'approved' ? <CheckCircle2 size={10} aria-hidden /> : status === 'sent' ? <Send size={10} aria-hidden /> : null} {label}
        </span>
        {total != null ? <span className="pfv-chip pfv-chip--version">{formatQuoteTotal(total)}</span> : null}
        {quote?.storagePath ? (
          <button type="button" className="pfv-act pfv-act--muted" onClick={() => void openQuote(quote)} title={quote.fileName}>
            Quote <ExternalLink size={10} aria-hidden />
          </button>
        ) : null}
      </span>
    );
  };

  const renderRound = (req: RfqRequest) => {
    const open = isRequestOpen(req, packages, submissions, projectApprovals);
    const collapsed = collapsedRounds.has(req.id ?? '');
    const due = req.quoteDeadline;
    const overdue = !!due && open && due.getTime() < Date.now();
    // Lanes this round covers that still exist on Files (re-request targets).
    const roundStreamIds = Array.from(new Set(req.lines.map(l => l.sourceStreamId).filter((id): id is string => !!id && laneByStreamId.has(id))));
    const roundHasNewer = roundStreamIds.some(id => laneByStreamId.get(id)?.state === 'newer_list');
    const anyQuoted = req.lines.some(l =>
      Object.values(l.packageIdByOrg ?? {}).some(pid => {
        const st = packageSupplierStatus(packageById.get(pid) ?? { id: pid, rfqProjectId: req.rfqProjectId, supplierOrgId: '' }, submissions, projectApprovals);
        return st === 'quoted';
      }),
    );
    const requestPackages = packages.filter((p) => p.id && (p.rfqRequestId === req.id || req.lines.some((line) => Object.values(line.packageIdByOrg ?? {}).includes(p.id!))));
    const archivedOnRequest = (structureId: string) => {
      const owners = requestPackages.filter((p) => (p.scopeStructures ?? []).some((s) => s.id === structureId));
      return owners.length > 0 && owners.every((p) => (p.archivedStructureIds ?? []).includes(structureId));
    };
    const lineArchived = (line: RfqRequest['lines'][number]) => {
      const ids = (line.structures ?? []).map((s) => s.id).filter(Boolean);
      return ids.length > 0 && ids.every((id) => archivedOnRequest(id));
    };
    const dropRows = supersededStructureArchiveRows(requestPackages, packages);
    const dropByStructureId = new Map(dropRows.map((row) => [row.id, row]));
    const runArchive = (busyKey: string, write: Promise<void>) => {
      setArchiveBusyId(busyKey);
      void write
        .catch((err) => {
          console.error('[archive structures]', err);
          window.alert('Could not update those structures. Try again.');
        })
        .finally(() => setArchiveBusyId(null));
    };
    return (
      <section key={req.id} className={`pfrq-round${open ? ' pfrq-round--open' : ''}`}>
        <button type="button" className="pfv-group-toggle pfrq-round-head" aria-expanded={!collapsed} onClick={() => toggleRound(req.id ?? '')}>
          <ChevronRight size={14} className={`pfv-caret${collapsed ? '' : ' pfv-caret--open'}`} aria-hidden />
          <span className="pfv-group-title">{req.label}</span>
          {rfqRequestStageLabel(req.stage) ? <span className="pfv-chip pfv-chip--lod">{rfqRequestStageLabel(req.stage)}</span> : null}
          <span className={`pfv-chip ${open ? 'pfv-chip--pending' : 'pfv-chip--rfq-approved'}`}>{open ? 'Awaiting quotes' : 'All answered'}</span>
          {due ? (
            <span className={`pfv-chip ${overdue ? 'pfv-chip--whole' : 'pfv-chip--structure'}`} title={due.toLocaleString()}>
              <CalendarClock size={10} aria-hidden /> Due {fmtDate(due)}
              {overdue ? ' · overdue' : ''}
            </span>
          ) : null}
          <span className="pfv-group-sub pfrq-round-meta">
            {req.sentByName || 'Staff'} · {formatDistanceToNow(req.sentAt, { addSuffix: true })} · {req.supplierOrgIds.length} supplier
            {req.supplierOrgIds.length !== 1 ? 's' : ''}
          </span>
        </button>
        {collapsed ? null : (
          <div className="pfv-group-body pfrq-round-body">
            {req.message ? <p className="pfrq-round-message">“{req.message}”</p> : null}
            <div className="pfrq-grid-wrap">
              <table className="pfrq-grid">
                <thead>
                  <tr>
                    <th scope="col">List</th>
                    {req.supplierOrgIds.map(org => (
                      <th key={org} scope="col">
                        {supplierNames.get(org) || 'Supplier'}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {req.lines.map(line => {
                    const file = fileById.get(line.sourceFileId);
                    const rel = releaseById.get(line.hubFileReleaseId);
                    const laneTitle = rel
                      ? laneReleaseTitle(rel, timberReleases)
                      : line.structures.length
                        ? line.structures.map(s => s.name).join(' + ')
                        : 'Whole project';
                    const archived = lineArchived(line);
                    const lineDrops = (line.structures ?? [])
                      .map((s) => dropByStructureId.get(s.id))
                      .filter((row): row is NonNullable<typeof row> => !!row);
                    return (
                      <tr key={line.key} className={archived ? 'pfrq-line--archived' : undefined}>
                        <th scope="row">
                          <div className="pfrq-line-title" title={rel ? `Staged release v${rel.releaseVersionIndex} on this work order` : undefined}>
                            {laneTitle}
                          </div>
                          <div className="pfv-row-meta">
                            {line.displayLabel}
                            {typeof line.versionIndex === 'number' ? ` · #${line.versionIndex}` : ''}
                            {line.versionLabel ? ` · ${line.versionLabel}` : ''}
                            {' · '}
                            {file ? (
                              <button type="button" className="pfv-link" onClick={() => onOpenSource(file)}>
                                Open list
                              </button>
                            ) : rel?.releasedUrl ? (
                              <a className="pfv-link" href={rel.releasedUrl} target="_blank" rel="noopener noreferrer">
                                Supplier copy
                              </a>
                            ) : null}
                          </div>
                          {(() => {
                            const lane = line.sourceStreamId ? laneByStreamId.get(line.sourceStreamId) : undefined;
                            const newer = !!lane && lane.state === 'newer_list';
                            const showRerequest = canRequest && !!lane?.head && !archived;
                            if (!canRequest && !archived && lineDrops.length === 0) return null;
                            if (!showRerequest && !archived && lineDrops.length === 0 && !newer) return null;
                            return (
                              <div className="pfrq-line-acts">
                                {archived ? <span className="pfrq-archived-label">Removed from this request</span> : null}
                                {newer && !archived ? (
                                  <span className="pfv-chip pfv-chip--structure" title={`Head is #${lane!.head!.versionIndex}; this round sent #${line.versionIndex ?? '?'}`}>
                                    Newer list · #{lane!.head!.versionIndex}
                                  </span>
                                ) : null}
                                {canRequest && archived ? (
                                  <button
                                    type="button"
                                    className="pfv-act pfv-act--muted"
                                    disabled={archiveBusyId === line.key}
                                    onClick={() => {
                                      const structureIds = line.structures.map((s) => s.id).filter(Boolean);
                                      const packageIds = requestPackages
                                        .filter((p) => (p.scopeStructures ?? []).some((s) => structureIds.includes(s.id)))
                                        .map((p) => p.id!)
                                        .filter(Boolean);
                                      runArchive(line.key, restorePackageStructureIds(packageIds, structureIds));
                                    }}
                                  >
                                    {archiveBusyId === line.key ? 'Restoring…' : 'Restore'}
                                  </button>
                                ) : null}
                                {canRequest && !archived
                                  ? lineDrops.map((row) => (
                                    <button
                                      key={row.id}
                                      type="button"
                                      className="pfrq-archive-btn"
                                      disabled={archiveBusyId === row.id}
                                      title="A later RFQ already asks for this structure. Drop it here so the supplier is not asked to quote it twice."
                                      onClick={() => runArchive(row.id, archivePackageStructureIds(row.packageIds, [row.id]))}
                                    >
                                      {archiveBusyId === row.id
                                        ? 'Removing…'
                                        : lineDrops.length > 1
                                          ? `Remove ${row.name}`
                                          : 'Remove from this request'}
                                    </button>
                                  ))
                                  : null}
                                {showRerequest ? (
                                  <button
                                    type="button"
                                    className="pfv-act pfv-act--muted"
                                    onClick={() => onNewRequest({ streamId: lane!.stream!.id! })}
                                    title={newer ? 'Request quotes on the newer version of this list' : 'Send this list to suppliers again'}
                                  >
                                    <RefreshCw size={10} aria-hidden /> {newer ? 'Request update…' : 'Re-request…'}
                                  </button>
                                ) : null}
                              </div>
                            );
                          })()}
                        </th>
                        {req.supplierOrgIds.map(org => {
                          const pid = line.packageIdByOrg?.[org];
                          if (!pid) {
                            return (
                              <td key={org} className="pfrq-cell pfrq-cell--none">
                                —
                              </td>
                            );
                          }
                          if (archived) {
                            return (
                              <td key={org} className="pfrq-cell pfrq-cell--none">
                                Removed
                              </td>
                            );
                          }
                          const pkg = packageById.get(pid) ?? { id: pid, rfqProjectId: req.rfqProjectId, supplierOrgId: org };
                          const status = packageSupplierStatus(pkg, submissions, projectApprovals);
                          const quote = submissions.find(s => s.rfqPackageId === pid && !s.notBidding && (!!s.storagePath || !!s.cncQuote));
                          return (
                            <td key={org} className={`pfrq-cell pfrq-cell--${status}`}>
                              {statusChip(status, quote ? submissionQuoteTotal(quote) : null, quote)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="pfrq-round-acts">
              {canRequest && roundStreamIds.length > 1 ? (
                <button
                  type="button"
                  className="pfv-act"
                  onClick={() => onNewRequest({ streamIds: roundStreamIds })}
                  title="Open the request wizard with every list in this round selected"
                >
                  <RefreshCw size={11} aria-hidden /> {roundHasNewer ? 'Request update for this round…' : 'Re-request this round…'}
                </button>
              ) : null}
              {anyQuoted && onOpenSuppliers ? (
                <button type="button" className="pfv-act" onClick={onOpenSuppliers} title="Approvals stay on the Suppliers tab for now">
                  Approve in Suppliers <ExternalLink size={11} aria-hidden />
                </button>
              ) : null}
            </div>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="pfv pfrq" data-tour="pf-rfqs">
      {error ? (
        <div className="pfv-feed-warning" role="alert">
          <AlertTriangle size={13} aria-hidden /> <span>Part of the RFQ history could not be loaded — this view may be incomplete.</span>
          {onRetry ? (
            <button type="button" className="pfv-act" onClick={onRetry}>
              Try again
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="pfv-summary">
        <span className="pfv-stat">
          {requests.length} round{requests.length !== 1 ? 's' : ''}
        </span>
        {openCount ? <span className="pfv-stat pfv-stat--active">{openCount} awaiting quotes</span> : null}
        <span className="pfv-summary-note">
          Batch structures into one RFQ, labelled Concept or Production. Each supplier gets one email with one combined list and uploads one quote. Approvals stay on the
          Suppliers tab for now.
        </span>
        <span className="pfv-summary-spacer" />
        {canRequest ? (
          <button type="button" className="pf-upload-btn pf-upload-btn--accent pfrq-new" onClick={() => onNewRequest()}>
            <Plus size={14} aria-hidden /> New request…
          </button>
        ) : null}
      </div>

      {stagedLanes.length > 0 ? (
        <section className="pfrq-staged" aria-label="Staged, not yet requested">
          <h4 className="pfrq-section-title">
            Staged, not yet requested
            <span className="pfv-group-sub">lists staged for suppliers that no one has been asked to quote</span>
            {canRequest && stagedLanes.length > 1 ? (
              <>
                <span className="pfv-summary-spacer" />
                <button type="button" className="pfv-act" onClick={() => onNewRequest({ releaseIds: stagedReleaseIds })} title="One request covering every staged list">
                  <Send size={11} aria-hidden /> Request all staged…
                </button>
              </>
            ) : null}
          </h4>
          <ul className="pfrq-staged-list">
            {stagedLanes.map(lane => {
              const ids = lane.releases.map(r => r.id).filter((id): id is string => !!id);
              return (
                <li key={lane.key} className="pfrq-staged-lane">
                  <div className="pfrq-staged-main">
                    <span className="pfrq-staged-title" title={lane.structureNames.length > 1 ? lane.structureNames.join(', ') : undefined}>
                      {lane.label}
                      <span className="pfv-chip pfv-chip--version" title={`Staged release v${lane.releases[0].releaseVersionIndex} on this work order`}>
                        {laneReleaseVersionLabel(lane.releases[0], timberReleases)}
                      </span>
                    </span>
                    <span className="pfrq-staged-releases">
                      {lane.releases.map(r => {
                        const file = fileById.get(r.sourceFileId);
                        return (
                          <span key={r.id} className="pfrq-staged-release">
                            <strong>{r.displayLabel}</strong>
                            {typeof r.versionIndex === 'number' ? ` · #${r.versionIndex}` : ''}
                            {r.createdAt instanceof Date ? ` · staged ${formatDistanceToNow(r.createdAt, { addSuffix: true })}` : ''}
                            {r.releasedByName ? ` by ${r.releasedByName}` : ''}
                            {file ? (
                              <>
                                {' · '}
                                <button type="button" className="pfv-link" onClick={() => onOpenSource(file)}>
                                  Open list
                                </button>
                              </>
                            ) : null}
                          </span>
                        );
                      })}
                    </span>
                  </div>
                  {canRequest ? (
                    <div className="pfrq-staged-acts">
                      <button type="button" className="pfv-act" onClick={() => onNewRequest({ releaseIds: ids })} title="Request quotes for this list">
                        Request quotes…
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <section className="pfrq-rounds" aria-label="Request rounds">
        <h4 className="pfrq-section-title">
          Request rounds
          <span className="pfv-group-sub">newest first</span>
        </h4>
        {requests.length === 0 ? (
          <div className="pfv-empty pfv-empty--compact">
            <p className="pfv-empty-title">No request rounds yet</p>
            <p className="pfv-empty-hint">
              Use <strong>New request…</strong> to pick the timber lists, the suppliers, and a due date. Each supplier receives one
              email with every structure they quote.
            </p>
          </div>
        ) : (
          requests.map(renderRound)
        )}
      </section>

      {hasLegacy ? (
        <section className="pfrq-legacy" aria-label="Earlier activity">
          <button type="button" className="pfv-group-toggle" aria-expanded={legacyOpen} onClick={() => setLegacyOpen(o => !o)}>
            <ChevronRight size={14} className={`pfv-caret${legacyOpen ? ' pfv-caret--open' : ''}`} aria-hidden />
            <span className="pfv-group-title">Earlier activity</span>
            <span className="pfv-group-sub">files staged or sent from the Suppliers tab</span>
          </button>
          {legacyOpen ? (
            <div className="pfrq-legacy-body">
              <ProjectRfqFilesView
                hubReleases={legacyReleases}
                vendorReleases={vendorReleases}
                packages={legacyPackages}
                streams={streams}
                files={files}
                structures={structures}
                supplierNames={supplierNames}
                submissions={submissions}
                projectApprovals={projectApprovals}
                loading={loading}
                onOpenSource={onOpenSource}
                onOpenStream={onOpenStream}
                onOpenSuppliers={onOpenSuppliers}
              />
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

export default ProjectRfqRequestsView;
